import { useEffect } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { ArrowDownRight, Mail, MapPin } from "lucide-react";
import Marquee from "@/components/Marquee";
import RegisterForm from "@/components/RegisterForm";

const HEADLINE = [
  { text: "TOGETHER," },
  { text: "WE CAN TURN" },
  { text: "EVERY SETTLEMENT" },
  { text: "INTO AN ACT" },
  { text: "OF KINDNESS", accent: true },
];

const ease = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Landing() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09 });
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div data-testid="landing-page" className="bg-cream text-navy min-h-screen overflow-x-clip">
      <div className="noise-overlay" aria-hidden="true" />

      <header data-testid="site-header" className="relative z-10 flex items-center justify-between px-6 md:px-12 py-3 md:py-4">
        <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
          <img data-testid="brand-logo" src="/logo.png" alt="Settle It Forward" className="h-12 md:h-14 w-auto" />
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="hidden md:block text-xs font-semibold tracking-[0.25em] text-steel uppercase pt-2"
        >
          A national kindness movement
        </motion.p>
      </header>

      <main className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 px-6 md:px-12 pt-2 md:pt-4 pb-5 md:pb-6 items-start">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="text-xs md:text-sm font-semibold tracking-[0.3em] text-brandred uppercase mb-3"
          >
            For Australians doing it tough
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display uppercase text-navy leading-[0.95] text-[10vw] sm:text-4xl xl:text-[2.75rem]"
          >
            {HEADLINE.map((line, i) => (
              <MaskedLine key={line.text} delay={0.15 + i * 0.11}>
                {line.text}
                {line.accent && <span className="text-brandred">.</span>}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8, ease }}
            className="mt-4 text-base md:text-lg text-steel max-w-md font-medium"
          >
            Practical support for Australians doing it tough.
          </motion.p>

          <div className="mt-5 md:mt-6">
            <RegisterForm />
          </div>
        </div>

        <div className="lg:col-span-5 order-first lg:order-none">
          <motion.div
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.4, ease }}
            className="relative w-fit mx-auto"
          >
            <div className="absolute -top-3 -right-3 md:-top-4 md:-right-4 w-full h-full border-2 border-brandred pointer-events-none" aria-hidden="true" />
            <div className="relative bg-navy/5">
              <img
                data-testid="hero-image"
                src="/hero.png"
                alt="Young girl in a cream knitted sweater hugging a large red knitted heart"
                className="w-auto h-auto max-h-[38vh] lg:max-h-[calc(100vh-22rem)] mx-auto object-contain"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.1, duration: 0.7, ease }}
              className="absolute top-4 -left-3 md:-left-6 bg-navy text-cream px-3 py-2 md:px-4 md:py-3 flex items-center gap-2 md:gap-3"
            >
              <ArrowDownRight className="w-4 h-4 md:w-5 md:h-5 text-brandred shrink-0" />
              <span className="font-display uppercase tracking-wide text-xs md:text-sm whitespace-nowrap">Turn settlement into kindness</span>
            </motion.div>
          </motion.div>
        </div>
      </main>

      <Marquee />

      <footer data-testid="site-footer" className="bg-navy text-cream">
        <div className="px-6 md:px-12 py-5 md:py-6 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <img data-testid="footer-brand-logo" src="/logo-light.png" alt="Settle It Forward" className="h-12 w-auto" />
            <p className="mt-4 text-cream/60 text-sm max-w-xs">
              Every settlement. <span className="text-brandred">Someone's tomorrow.</span>
            </p>
          </div>
          <div className="flex flex-col gap-4 text-sm">
            <a
              data-testid="footer-contact-email"
              href="mailto:hello@settleitforward.au"
              className="flex items-center gap-3 text-cream/80 hover:text-brandred transition-colors duration-200"
            >
              <Mail className="w-4 h-4" /> hello@settleitforward.au
            </a>
            <p className="flex items-center gap-3 text-cream/80">
              <MapPin className="w-4 h-4" /> Australia-wide
            </p>
          </div>
        </div>
        <div className="border-t border-cream/15 px-6 md:px-12 py-3 flex flex-col sm:flex-row justify-between gap-2 text-xs text-cream/50 tracking-wide">
          <span>© 2026 Settle It Forward</span>
          <span>Practical support for Australians doing it tough</span>
        </div>
      </footer>
    </div>
  );
}
